import { PlusIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { SimpleTable } from "@/core/components/simple-table";
import { Alert } from "@/core/components/ui/alert/alert";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent } from "@/core/components/ui/card";
import {
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { getPayrollRosterApiErrorMessage } from "./api-errors";
import { ROSTER_NAME_COLUMN_ID } from "./cols";
import { ImportValuesDialog } from "./import-values-dialog";
import {
  useDiscardPayrollRosterDraft,
  usePublishPayrollRoster,
  useSavePayrollRosterDraft,
  useSyncPayrollRosterTechnicians,
} from "./query";
import { RosterChangesPanel } from "./roster-changes-panel";
import { RosterDisplayControls } from "./roster-display-controls";
import { RosterEditDrawer } from "./roster-edit-drawer";
import { RosterFilterEmptyState } from "./roster-filter-empty-state";
import { RosterMemberSummary } from "./roster-member-summary";
import { RosterMoreActionsMenu } from "./roster-more-actions-menu";
import type {
  PayrollTeam,
  PayrollRoster,
  PayrollRosterContract,
  PayrollRosterDraftMember as DraftMember,
  PayrollRosterEditTarget as EditTarget,
  PayrollRosterField,
  PayrollRosterMember,
  PayrollRosterRowFocus,
} from "./types";
import { usePayrollRosterColumns } from "./use-payroll-roster-columns";
import { useRosterFilters } from "./use-roster-filters";
import { useRosterUsers } from "./use-roster-users";
import {
  applyBulkImportToMembers,
  contractColumns,
  contractFieldOptions,
  createPayrollRosterDraftMembers,
  createPayrollRosterMember,
  createPayrollRosterSuggestionDrafts,
  diffRoster,
  editedMemberIds,
  filterPayrollRosterDraftsByFocus,
  hasPayrollRosterMemberErrors,
  isGovernedContract,
  membersNeedingReassignment,
  validatePayrollRosterMember,
  type BulkImportRow,
} from "./utils";

interface PayrollRosterEditorProps {
  roster: PayrollRoster;
  businessId: string;
  canSyncTechnicians?: boolean;
}

const EMPTY_CONTRACT: PayrollRosterContract = {};
const EMPTY_TEAMS: PayrollTeam[] = [];
const EMPTY_EXTERNAL_IDS: string[] = [];

export const PayrollRosterEditor = ({
  roster,
  businessId,
  canSyncTechnicians = false,
}: PayrollRosterEditorProps) => {
  const { userNames } = useRosterUsers(businessId);
  const teams = roster.available_teams ?? EMPTY_TEAMS;
  const contract = roster.contract ?? EMPTY_CONTRACT;
  const governed = isGovernedContract(contract);
  // The published roster the working copy is diffed against (persisted, so the
  // "unpublished changes" state survives a refresh). Empty until first publish.
  const baseline = roster.baseline;

  // The header anchors to the live PUBLISHED version — the stable thing the
  // owner recognizes — rather than the working copy's own status. An in-progress
  // draft bumps the working version to N+1 with no published_at; surfacing that
  // in the badge (instead of the change-bar) makes an unedited draft look like a
  // broken "unpublished" state with no way back. `baseline` is populated iff a
  // published version exists, so this is null only before the first publish.
  const publishedVersion = baseline?.version_number ?? null;
  const publishedAt = baseline?.published_at ?? null;

  // For a GOVERNED business the editor columns are derived from the CONTRACT:
  // every contract field is a column (in contract order), any the roster lacks
  // rendered empty and any stale field the contract dropped removed — so the
  // grid (and the save payload) exactly mirror the contract, with no manual
  // "add column" step. Ungoverned rosters keep their stored fields untouched.
  const fields = useMemo(
    () => contractColumns(roster.fields, contract),
    [roster.fields, contract],
  );
  const draftMembers = useMemo(
    () => createPayrollRosterDraftMembers(roster.members),
    [roster.members],
  );
  // Which attention group the table is narrowed to, if any. One at a time:
  // "add these" and "remove these" are separate jobs.
  const [rowFocus, setRowFocus] = useState<PayrollRosterRowFocus>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dismissingSuggestionId, setDismissingSuggestionId] = useState<
    string | null
  >(null);
  const isDismissingSuggestion = dismissingSuggestionId != null;
  // ServiceTitan IDs the owner has said do not belong on payroll. Seeded from
  // the working roster and sent back on every draft save, so a dismissal
  // survives alongside the member edits it publishes with.
  const dismissedExternalIds =
    roster.removed_member_external_ids ?? EMPTY_EXTERNAL_IDS;
  // The one right-side drawer that can be open: either editing a row or
  // reviewing changes — never both (see the shared SimpleSidebarLayout below).
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const { mutateAsync: saveRosterDraft, isPending: isSavingDraft } =
    useSavePayrollRosterDraft(businessId);
  const publishRoster = usePublishPayrollRoster(businessId);
  const discardDraft = useDiscardPayrollRosterDraft(businessId);
  const syncTechnicians = useSyncPayrollRosterTechnicians(businessId);
  // One in-flight guard across every roster action. The ref is the synchronous
  // re-entry guard used in handlers; `rosterMutationPending` is the reactive flag
  // for disabling UI while save, publish, discard, or sync work is in progress.
  const mutatingRef = useRef(false);
  const rosterMutationPending =
    isDismissingSuggestion ||
    isSavingDraft ||
    publishRoster.isPending ||
    discardDraft.isPending ||
    syncTechnicians.isPending;

  // Persist the full working roster. The mutation writes the server response to
  // the roster query, so this component always renders the current server state.
  // The `fields` sent include contract fields that the roster did not yet have.
  const persistDraft = useCallback(
    async (nextDraftMembers: DraftMember[], nextDismissed?: string[]) => {
      if (mutatingRef.current) return false;

      mutatingRef.current = true;
      try {
        const memberExternalIds = new Set(
          nextDraftMembers.map(
            ({ member }) => member.service_titan_employee_id,
          ),
        );
        await saveRosterDraft({
          roster: {
            fields,
            members: nextDraftMembers.map(({ member }) => member),
            removed_member_external_ids: (
              nextDismissed ?? dismissedExternalIds
            ).filter((externalId) => !memberExternalIds.has(externalId)),
          },
        });
        return true;
      } catch (error) {
        toast.error("Changes could not be saved", {
          description: getPayrollRosterApiErrorMessage(error),
        });
        return false;
      } finally {
        mutatingRef.current = false;
      }
    },
    [dismissedExternalIds, fields, saveRosterDraft],
  );

  const fieldOptions = useMemo(
    () =>
      Object.fromEntries(
        fields.map((field) => [
          field.key,
          contractFieldOptions(field.key, contract, field.options),
        ]),
      ),
    [fields, contract],
  );

  // Global validity gate for Review/Publish: bulk imports can introduce invalid
  // values without ever opening the drawer, so keep validating every draft.
  const hasErrors = useMemo(
    () =>
      draftMembers.some(({ member }) =>
        hasPayrollRosterMemberErrors(
          validatePayrollRosterMember(member, fields, contract),
        ),
      ),
    [draftMembers, fields, contract],
  );

  // Governed-only: members whose saved value is no longer valid under the
  // contract (a removed/renamed option, a newly-required blank, a type change).
  // This is the subset of `hasErrors` that a contract change caused — it powers
  // the top-of-page banner that makes the blocked-publish reason legible.
  const needsReassignment = useMemo(
    () =>
      governed
        ? membersNeedingReassignment(
            draftMembers.map(({ member }) => member),
            fields,
            contract,
          )
        : [],
    [governed, draftMembers, fields, contract],
  );

  // The exact member objects that need reassignment, held by reference so the
  // read-only table can flag their rows (gold "warning" row + a Name-cell
  // badge). `needsReassignment` filters the draft members, so identity holds.
  const flaggedMembers = useMemo(
    () => new Set(needsReassignment),
    [needsReassignment],
  );

  const changes = useMemo(() => {
    const published = baseline?.removed_member_external_ids ?? [];
    const names = new Map<string, string>();
    (roster.missing_user_suggestions ?? []).forEach((suggestion) => {
      if (!names.has(suggestion.service_titan_employee_id)) {
        names.set(
          suggestion.service_titan_employee_id,
          suggestion.display_name,
        );
      }
    });

    return diffRoster(
      draftMembers.map(({ member, isNew }) => ({ member, isNew })),
      baseline?.members ?? [],
      fields,
      {
        added: dismissedExternalIds.filter((id) => !published.includes(id)),
        names,
      },
      userNames,
    );
  }, [
    draftMembers,
    baseline,
    fields,
    dismissedExternalIds,
    roster.missing_user_suggestions,
    userNames,
  ]);
  const hasChanges = changes.length > 0;

  // Draft ids carrying unpublished edits (vs the published baseline), so the
  // read-only table can flag them.
  const editedIds = useMemo(
    () => editedMemberIds(draftMembers, baseline?.members ?? [], fields),
    [draftMembers, baseline, fields],
  );

  const suggestionDrafts = useMemo<DraftMember[]>(
    () =>
      createPayrollRosterSuggestionDrafts(
        roster.missing_user_suggestions ?? [],
        draftMembers,
        dismissedExternalIds,
      ),
    [dismissedExternalIds, draftMembers, roster.missing_user_suggestions],
  );
  const displayedDrafts = useMemo(
    () => [...draftMembers, ...suggestionDrafts],
    [draftMembers, suggestionDrafts],
  );
  const memberCount = draftMembers.length;
  const suggestionCount = suggestionDrafts.length;
  // Members Volca can no longer match to an active user. The API recommends
  // removing them; it never blocks publishing on them.
  const departedIds = useMemo(
    () => new Set(roster.departed_member_ids ?? []),
    [roster.departed_member_ids],
  );
  const departedCount = useMemo(
    () =>
      draftMembers.filter(({ member }) =>
        departedIds.has(member.service_titan_employee_id),
      ).length,
    [draftMembers, departedIds],
  );
  // Attention rows sort to the end of the list, so on a large roster they land
  // on the last page. Narrowing to one group puts the people who still need
  // work one click away rather than a page hunt.
  const rosterFilters = useRosterFilters(displayedDrafts, fields, {
    fieldOptions,
    teams,
    businessId,
  });
  const { search, activeFilterCount } = rosterFilters;
  const filteredDrafts = useMemo(
    () =>
      filterPayrollRosterDraftsByFocus(
        rosterFilters.filteredRows,
        rowFocus,
        departedIds,
      ),
    [rosterFilters.filteredRows, rowFocus, departedIds],
  );
  const hasActiveDisplayFilters =
    rosterFilters.hasActiveFilters || rowFocus !== null;
  const scrollResetKey = `${rosterFilters.filterKey}-${rowFocus ?? "all"}-${pagination.pageIndex}-${pagination.pageSize}`;

  const openDraft = useCallback((draft: DraftMember) => {
    if (mutatingRef.current) return;
    setReviewOpen(false);
    setEditTarget(
      draft.suggestion
        ? { mode: "suggestion", userId: draft.suggestion.user_id }
        : { mode: "edit", draftId: draft.id },
    );
  }, []);

  const openAdd = () => {
    if (mutatingRef.current) return;
    setReviewOpen(false);
    setEditTarget({ mode: "new" });
  };

  const clearDisplayFilters = () => {
    rosterFilters.clearFilters();
    setRowFocus(null);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const toggleRowFocus = (focus: Exclude<PayrollRosterRowFocus, null>) => {
    setRowFocus((current) => (current === focus ? null : focus));
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const closeDrawer = () => setEditTarget(null);

  // Keep the currently displayed roster authoritative until the full working
  // copy has persisted. The drawer remains open and blocked while saving, then
  // the returned roster replaces the table in one update.
  const saveEdit = async (member: PayrollRosterMember) => {
    if (!editTarget || mutatingRef.current) return;
    const adding =
      editTarget.mode === "new" || editTarget.mode === "suggestion";
    const next = adding
      ? [
          ...draftMembers,
          { id: `new-${crypto.randomUUID()}`, isNew: true, member },
        ]
      : draftMembers.map((draft) =>
          draft.id === editTarget.draftId ? { ...draft, member } : draft,
        );
    if (await persistDraft(next)) setEditTarget(null);
  };

  // Remove a person from the roster — how someone comes off payroll. A brand-new
  // unsaved person isn't in the draft yet, so we just close; an existing person
  // is dropped from the working draft and persisted (reversible via Discard).
  const removeMember = async () => {
    if (!editTarget || mutatingRef.current) return;
    if (editTarget.mode !== "edit") {
      setEditTarget(null);
      return;
    }
    const { draftId } = editTarget;
    const next = draftMembers.filter((draft) => draft.id !== draftId);
    if (await persistDraft(next)) setEditTarget(null);
  };

  // Says this active technician does not belong on payroll at all — a test or
  // office account, typically. Recorded on the draft and published with
  // everything else, so it shows in the review first. Reversible: adding them
  // to a later version clears it.
  const dismissSuggestion = useCallback(
    async (draft: DraftMember | undefined) => {
      if (mutatingRef.current) return;

      const suggestion = draft?.suggestion;
      if (!suggestion) return;

      setDismissingSuggestionId(suggestion.service_titan_employee_id);
      try {
        const next = [
          ...dismissedExternalIds,
          suggestion.service_titan_employee_id,
        ];
        if (await persistDraft(draftMembers, next)) {
          if (suggestionDrafts.length === 1) setRowFocus(null);
          setEditTarget(null);
        }
      } finally {
        setDismissingSuggestionId(null);
      }
    },
    [dismissedExternalIds, draftMembers, persistDraft, suggestionDrafts.length],
  );

  const applyImport = async (
    field: PayrollRosterField,
    rows: BulkImportRow[],
  ) => {
    const { members, matched, unmatched, invalid } = applyBulkImportToMembers(
      draftMembers.map(({ member }) => member),
      field,
      rows,
    );
    if (matched.length === 0) return { matched, unmatched, invalid };

    // members preserves order/length, so zip it back onto the drafts.
    const next = draftMembers.map((draft, index) => ({
      ...draft,
      member: members[index],
    }));
    const saved = await persistDraft(next);
    if (!saved) throw new Error("Roster import could not be saved");
    return { matched, unmatched, invalid };
  };

  const openReview = () => {
    if (mutatingRef.current) return;
    if (hasErrors) {
      toast.error("Complete the required roster fields before publishing.");
      return;
    }
    setEditTarget(null);
    setReviewOpen(true);
  };

  // Promote the persisted draft to published. Uses mutateAsync in an async
  // handler rather than per-call mutate callbacks: publishing creates a new
  // published version, so the awaited canonical refetch changes the page-level
  // editor key and remounts this component. That unmounts the mutation observer,
  // and TanStack only fires per-call callbacks while the observer still has
  // listeners — so a mutate({ onSuccess, onSettled }) form would drop the success
  // toast and cleanup. The awaited chain below runs independently of the observer.
  // The explicit reset also covers the no-remount case.
  const publish = async () => {
    if (mutatingRef.current) return;
    mutatingRef.current = true;
    try {
      await publishRoster.mutateAsync();
      setEditTarget(null);
      setReviewOpen(false);
      toast.success("Payroll roster published");
    } catch (error) {
      toast.error("Payroll roster could not be published", {
        description: getPayrollRosterApiErrorMessage(error),
      });
    } finally {
      mutatingRef.current = false;
    }
  };

  // Discard the persisted draft. The query hook waits for the canonical roster
  // refetch before this success callback closes the review panel.
  const discard = () => {
    if (mutatingRef.current) return;
    mutatingRef.current = true;
    discardDraft.mutate(undefined, {
      onSuccess: () => {
        setEditTarget(null);
        setReviewOpen(false);
        toast.success("Draft changes discarded");
      },
      onError: (error) => {
        toast.error("Changes could not be discarded", {
          description: getPayrollRosterApiErrorMessage(error),
        });
      },
      onSettled: () => {
        mutatingRef.current = false;
      },
    });
  };

  const syncUsers = async () => {
    if (mutatingRef.current) return;
    mutatingRef.current = true;
    syncTechnicians.reset();
    try {
      await syncTechnicians.mutateAsync();
      toast.success("ServiceTitan technicians synced");
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      toast.error("ServiceTitan technicians could not be synced", {
        description: getPayrollRosterApiErrorMessage(error),
      });
    } finally {
      mutatingRef.current = false;
    }
  };

  const { columns, tableMinWidth } = usePayrollRosterColumns({
    fields,
    teams,
    draftMembers: displayedDrafts,
    editedIds,
    flaggedMembers,
    departedIds,
    onOpen: openDraft,
    onDismissSuggestion: dismissSuggestion,
    dismissingSuggestionId,
    busy: rosterMutationPending,
  });

  const editingDraft =
    editTarget?.mode === "edit"
      ? draftMembers.find((draft) => draft.id === editTarget.draftId)
      : undefined;
  const editingSuggestion =
    editTarget?.mode === "suggestion"
      ? suggestionDrafts.find(
          (draft) => draft.suggestion?.user_id === editTarget.userId,
        )
      : undefined;
  const drawerInitialMember =
    editTarget?.mode === "new"
      ? createPayrollRosterMember(fields)
      : editTarget?.mode === "suggestion"
        ? editingSuggestion?.member
        : editingDraft?.member;
  const drawerKey =
    editTarget?.mode === "new"
      ? "new"
      : editTarget?.mode === "suggestion"
        ? `suggestion-${editTarget.userId}`
        : editTarget?.draftId;

  return (
    <SimpleSidebarLayout
      open={editTarget != null || reviewOpen}
      onOpenChange={(open) => {
        if (mutatingRef.current) return;
        if (!open) {
          setEditTarget(null);
          setReviewOpen(false);
        }
      }}
      sidebarWidth="480px"
      className="!h-full"
    >
      <SimpleSidebarContent removeScrollArea className="flex flex-col">
        <Stack gap="md" fullWidth className="h-full min-h-0">
          <Stack gap="xs" fullWidth className="shrink-0">
            <Stack
              direction="row"
              align="center"
              justify="between"
              gap="md"
              fullWidth
              className="flex-wrap"
            >
              <Stack direction="row" align="center" gap="sm">
                <Typography variant="titleW2">Payroll roster</Typography>
                {publishedVersion != null ? (
                  <>
                    <Badge variant="gray" size="sm">
                      v{publishedVersion}
                    </Badge>
                    <Badge variant="green" size="sm">
                      Published
                    </Badge>
                  </>
                ) : (
                  <Badge variant="gray" size="sm">
                    Draft
                  </Badge>
                )}
              </Stack>
              <Stack direction="row" align="center" gap="sm">
                <Button
                  size="sm"
                  leftIcon={<PlusIcon />}
                  variant="secondary"
                  disabled={rosterMutationPending}
                  onClick={openAdd}
                >
                  Add person
                </Button>
                <RosterMoreActionsMenu
                  onImportValues={() => setImportOpen(true)}
                  canImportValues={fields.length > 0}
                  onSyncTechnicians={canSyncTechnicians ? syncUsers : undefined}
                  isSyncing={syncTechnicians.isPending}
                  busy={rosterMutationPending}
                />
              </Stack>
            </Stack>
            <RosterMemberSummary
              memberCount={memberCount}
              displayedCount={displayedDrafts.length}
              filteredCount={filteredDrafts.length}
              suggestionCount={suggestionCount}
              departedCount={departedCount}
              focus={rowFocus}
              hasActiveFilters={hasActiveDisplayFilters}
              publishedAt={publishedAt}
              onFocusChange={toggleRowFocus}
            />
          </Stack>

          {syncTechnicians.isError && (
            <Alert variant="destructive" className="shrink-0">
              ServiceTitan technicians could not be synced.{" "}
              {getPayrollRosterApiErrorMessage(syncTechnicians.error)}
            </Alert>
          )}

          {/* Contract-delta banner: a field or pay-plan option changed under
              the roster, so some saved values are no longer valid. Publish is
              already blocked by `hasErrors`; this makes the reason legible and
              points the owner at the flagged rows (each shows its inline error
              in the drawer). Governed rosters only. */}
          {needsReassignment.length > 0 && (
            <Alert variant="destructive" className="shrink-0">
              <Stack gap="xs">
                <Typography variant="subtitleW3">
                  {needsReassignment.length}{" "}
                  {needsReassignment.length === 1
                    ? "person needs"
                    : "people need"}{" "}
                  attention
                </Typography>
                <Typography variant="bodyW3" textColor="secondary">
                  A field or pay plan changed and their saved value is no longer
                  valid. Open each flagged person and pick a new value before
                  publishing.
                </Typography>
              </Stack>
            </Alert>
          )}

          {/* Unpublished-changes bar: only shown when there are changes, and
              kept at the TOP so the primary Review button never collides with
              the floating Pylon chat widget in the bottom-right. */}
          {hasChanges && (
            <Stack
              direction="row"
              align="center"
              justify="between"
              gap="md"
              fullWidth
              className="shrink-0 flex-wrap rounded-xl border border-stroke-tertiary bg-gold-5 px-4 py-3"
            >
              <Stack gap="none">
                <Typography variant="subtitleW3">
                  {changes.length} unpublished{" "}
                  {changes.length === 1 ? "change" : "changes"}
                </Typography>
                <Typography variant="bodyW3" className="text-tertiary-text">
                  Publishing saves a new version; past runs keep the version
                  they used.
                </Typography>
              </Stack>
              <Stack direction="row" align="center" gap="sm">
                <Button
                  variant="text"
                  size="sm"
                  disabled={rosterMutationPending}
                  loading={discardDraft.isPending}
                  onClick={discard}
                >
                  Discard changes
                </Button>
                <Button
                  size="sm"
                  disabled={rosterMutationPending}
                  onClick={openReview}
                >
                  Review changes
                </Button>
              </Stack>
            </Stack>
          )}

          <RosterDisplayControls
            {...rosterFilters.controls}
            onSearchChange={(value) => {
              rosterFilters.controls.onSearchChange(value);
              setPagination((current) => ({ ...current, pageIndex: 0 }));
            }}
            onFiltersChange={(filters) => {
              rosterFilters.controls.onFiltersChange(filters);
              setPagination((current) => ({ ...current, pageIndex: 0 }));
            }}
          />

          {/* The table card fills the remaining vertical space inside the page
              padding and scrolls internally (flex-1 + min-h-0). */}
          <Card className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
            <CardContent className="flex min-h-0 flex-1 flex-col p-0">
              {displayedDrafts.length === 0 ? (
                <Stack
                  align="center"
                  justify="center"
                  className="min-h-0 w-full flex-1 border-t border-stroke-tertiary px-6 text-center"
                >
                  <Typography variant="subtitleW1">
                    No roster members yet
                  </Typography>
                  <Typography className="mt-2" textColor="secondary">
                    Add the first person to start this payroll roster.
                  </Typography>
                </Stack>
              ) : filteredDrafts.length === 0 ? (
                <RosterFilterEmptyState
                  search={search}
                  activeFilterCount={
                    activeFilterCount + (rowFocus === null ? 0 : 1)
                  }
                  onClear={clearDisplayFilters}
                />
              ) : (
                // SimpleTable needs a full-width, definite-height container; the
                // flex-1 + min-h-0 gives it the rest of the card height so it
                // scrolls internally. The whole row opens the edit drawer
                // (onRowClick); scrollContentStyle sets the dynamic min-width so
                // wide rosters scroll sideways.
                <Stack
                  gap="none"
                  fullWidth
                  className="min-h-0 flex-1 border-y border-stroke-tertiary"
                >
                  <SimpleTable
                    data={filteredDrafts}
                    columns={columns}
                    getRowId={(draft) => draft.id}
                    enablePagination
                    paginationState={pagination}
                    onPaginationStateChange={setPagination}
                    // Members whose saved value no longer fits the contract get
                    // the built-in gold "warning" row so they're spottable
                    // without opening each one.
                    getRowVariant={(draft) =>
                      draft.suggestion
                        ? "destructive"
                        : flaggedMembers.has(draft.member) ||
                            departedIds.has(
                              draft.member.service_titan_employee_id,
                            )
                          ? "warning"
                          : undefined
                    }
                    onRowClick={rosterMutationPending ? undefined : openDraft}
                    scrollResetKey={scrollResetKey}
                    className="h-full w-full"
                    scrollContentStyle={{ minWidth: tableMinWidth }}
                    // Keep the person's name in view while scrolling the wide
                    // (13+ column) roster sideways — it's the identity anchor.
                    columnPinning={{
                      left: [ROSTER_NAME_COLUMN_ID],
                    }}
                    respectColumnWidths
                  />
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </SimpleSidebarContent>

      {editTarget != null && drawerInitialMember ? (
        <RosterEditDrawer
          key={drawerKey}
          businessId={businessId}
          teams={teams}
          title={
            editTarget.mode === "edit" ? "Edit person" : "Add person to roster"
          }
          fields={fields}
          fieldOptions={fieldOptions}
          contract={contract}
          initialMember={drawerInitialMember}
          canRemove={editTarget.mode === "edit"}
          onDismiss={
            editTarget.mode === "suggestion"
              ? () => dismissSuggestion(editingSuggestion)
              : undefined
          }
          showInitialErrors={editTarget.mode === "edit"}
          saving={isSavingDraft || isDismissingSuggestion}
          dismissing={isDismissingSuggestion}
          onSave={saveEdit}
          onCancel={closeDrawer}
          onRemove={removeMember}
        />
      ) : reviewOpen ? (
        <RosterChangesPanel
          changes={changes}
          publishing={publishRoster.isPending}
          onClose={() => setReviewOpen(false)}
          onPublish={publish}
        />
      ) : null}

      <ImportValuesDialog
        open={importOpen}
        fields={fields}
        saving={isSavingDraft}
        onOpenChange={setImportOpen}
        onApply={applyImport}
      />
    </SimpleSidebarLayout>
  );
};
